import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * El comportamiento de una tira de fotos que se corre de lado.
 *
 * Es un hook y no un componente a proposito: la galeria pone sus flechas
 * arriba, al lado del titulo, y un servicio las pone en otra parte. Un
 * componente comun obligaria a las dos a dibujarse igual. Asi cada una arma su
 * propio HTML y comparten lo unico que de verdad es comun, que es la logica:
 * medir, arrastrar y saber si se llego a alguna punta.
 *
 * Devuelve:
 *   tira, barra    refs para la tira y la barrita de avance (la barra es opcional)
 *   alInicio       si no queda nada hacia atras, para apagar la flecha
 *   alFinal        idem hacia adelante
 *   desplazar(d)   corre una foto hacia -1 o +1
 *   huboArrastre   ref: si el ultimo gesto fue arrastre y no clic
 */
export default function useCarrusel() {
  const tira = useRef(null);
  const barra = useRef(null);
  const [alInicio, setAlInicio] = useState(true);
  const [alFinal, setAlFinal] = useState(false);

  /*
   * Si el ultimo gesto fue un arrastre y no un clic.
   *
   * Arrastrar la tira termina en un `click` sobre la foto donde quedo el dedo,
   * asi que sin esto se abriria el visor cada vez que alguien la corre de lado.
   * Se pone en falso en cada `pointerdown`, no al soltar: el `click` cae entre
   * el `pointerup` y el `pointerdown` siguiente, asi que cuando se lee, el
   * valor todavia es el del gesto que acaba de terminar.
   */
  const huboArrastre = useRef(false);

  /* Mantiene al dia las flechas y la barrita de avance. */
  const medir = useCallback(() => {
    const el = tira.current;
    const b = barra.current;
    if (!el) return;

    const recorrido = el.scrollWidth - el.clientWidth;
    setAlInicio(el.scrollLeft <= 2);
    setAlFinal(recorrido <= 2 || el.scrollLeft >= recorrido - 2);

    if (!b) return;
    const anchoBarra = b.clientWidth;
    const porcion = el.scrollWidth > 0 ? Math.min(1, el.clientWidth / el.scrollWidth) : 1;
    const avance = recorrido > 0 ? el.scrollLeft / recorrido : 0;
    const anchoPorcion = Math.max(24, anchoBarra * porcion);
    b.style.setProperty('--porcion', `${anchoPorcion}px`);
    b.style.setProperty('--posicion', `${avance * (anchoBarra - anchoPorcion)}px`);
  }, []);

  useEffect(() => {
    const el = tira.current;
    if (!el) return undefined;

    medir();
    el.addEventListener('scroll', medir, { passive: true });
    window.addEventListener('resize', medir);
    return () => {
      el.removeEventListener('scroll', medir);
      window.removeEventListener('resize', medir);
    };
  }, [medir]);

  /* Arrastrar con el mouse, como en una mesa de fotos. El tacto ya lo hace solo. */
  useEffect(() => {
    const el = tira.current;
    if (!el) return undefined;

    let activo = false;
    let xInicial = 0;
    let scrollInicial = 0;

    const abajo = (e) => {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      activo = true;
      huboArrastre.current = false;
      xInicial = e.clientX;
      scrollInicial = el.scrollLeft;
    };

    const mover = (e) => {
      if (!activo) return;
      const recorrido = e.clientX - xInicial;
      if (Math.abs(recorrido) > 4) {
        el.dataset.arrastrando = 'si';
        huboArrastre.current = true;
      }
      el.scrollLeft = scrollInicial - recorrido;
    };

    const soltar = () => {
      if (!activo) return;
      activo = false;
      delete el.dataset.arrastrando;
    };

    el.addEventListener('pointerdown', abajo);
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
    return () => {
      el.removeEventListener('pointerdown', abajo);
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
    };
  }, []);

  const desplazar = useCallback((direccion) => {
    const el = tira.current;
    if (!el) return;
    const item = el.querySelector('.tira__item');
    const paso = item ? item.getBoundingClientRect().width + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: direccion * paso, behavior: 'smooth' });
  }, []);

  return { tira, barra, alInicio, alFinal, desplazar, huboArrastre };
}
